<template>
  <div>
    <page-loading
      v-if="loading"
      message="Carregando loja do parceiro"
      subtitle="Buscando dados para configuração"
    />
    <template v-else>
      <v-dialog v-model="dialog" width="800">
        <v-card width="800" class="d-flex align-center justify-center">
          <img
            v-if="dialogImageSrc"
            class="preview-dialog-img"
            :src="dialogImageSrc"
            alt="Visualização da imagem"
          />
        </v-card>
      </v-dialog>

      <div class="header">
        <div>
          <h2 class="page-title">Configurar loja</h2>
          <p class="page-subtitle mb-0">
            {{ partner.name || "Parceiro" }}
          </p>
        </div>
        <v-btn text color="#e5183d" @click="goBack">
          <v-icon left>mdi-arrow-left</v-icon>
          Voltar para a listagem
        </v-btn>
      </div>

      <v-tabs
        v-model="tab"
        background-color="white"
        color="#e5183d"
        slider-color="#e5183d"
        show-arrows
        class="mb-4"
      >
        <v-tab>
          <v-icon left>mdi-account-details</v-icon>
          Dados principais
        </v-tab>
        <v-tab>
          <v-icon left>mdi-map-marker</v-icon>
          Endereço
        </v-tab>
        <v-tab>
          <v-icon left>mdi-clock-outline</v-icon>
          Horários
        </v-tab>
        <v-tab>
          <v-icon left>mdi-information</v-icon>
          Informações adicionais
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item>
          <v-card>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    :label="partner.typeCompany == 'PJ' ? 'Razão social' : 'Nome completo'"
                    v-model="partner.name"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :label="
                      partner.typeCompany == 'PJ'
                        ? 'Nome fantasia'
                        : 'Como quer ser chamado'
                    "
                    v-model="partner.nickname"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :label="partner.typeCompany == 'PJ' ? 'CNPJ' : 'CPF'"
                    v-model="partner.document"
                    :v-mask="
                      partner.typeCompany == 'PJ'
                        ? '##.###.###/####-##'
                        : '###.###.###-##'
                    "
                    outlined
                    dense
                    :disabled="isReadOnly"
                    @input="syncCompanyType"
                  />
                </v-col>
                <v-col cols="12" md="4" v-if="partner.typeCompany === 'PJ'">
                  <v-text-field
                    label="CNAE"
                    v-model="partner.cnaeCode"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    label="Email"
                    v-model="partner.email"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    label="Telefone"
                    v-model="partner.phone"
                    v-mask="'(##) #####-####'"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    label="Categoria"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    v-model="partner.categoryId"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    @blur="handleAddress"
                    label="CEP"
                    v-model="partner.address.zipCode"
                    v-mask="'#####-###'"
                    outlined
                    dense
                    hint="Digite o CEP para buscar automaticamente"
                    persistent-hint
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="Logradouro"
                    v-model="partner.address.street"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    label="Número"
                    v-model="partner.address.addressNumber"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    label="Bairro"
                    v-model="partner.address.neighborhood"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    label="Complemento"
                    v-model="partner.address.complement"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    label="Cidade"
                    disabled
                    readonly
                    v-model="partner.address.cityName"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="1">
                  <v-text-field
                    label="UF"
                    disabled
                    readonly
                    v-model="partner.address.state"
                    outlined
                    dense
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card class="mb-4" outlined>
            <v-card-title class="subtitle-1">Adicionar horário</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <v-select
                    :items="days"
                    label="Dia da semana"
                    v-model="businessHoursDay"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    type="time"
                    label="Horário de abertura"
                    v-model="businessHoursStartTime"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    type="time"
                    label="Horário de fechamento"
                    v-model="businessHoursEndTime"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="1" class="d-flex align-end">
                  <v-btn
                    :disabled="
                      isReadOnly ||
                      !businessHoursDay ||
                      !businessHoursStartTime ||
                      !businessHoursEndTime
                    "
                    color="#e5183d"
                    dark
                    icon
                    @click="handleAddBusinessHours"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card>
            <v-card-title class="subtitle-1">Horários configurados</v-card-title>
            <v-data-table
              :headers="headersHours"
              :items="partner.businessHours"
              :items-per-page="7"
              hide-default-footer
              no-data-text="Nenhum horário configurado"
            >
              <template v-slot:body="{ items }">
                <tbody>
                  <tr v-if="!items.length">
                    <td colspan="4" class="text-center grey--text py-6">
                      Nenhum horário configurado
                    </td>
                  </tr>
                  <tr v-for="(item, index) in items" :key="index">
                    <td>{{ item.day }}</td>
                    <td>{{ item.startTime }}</td>
                    <td>{{ item.endTime }}</td>
                    <td class="text-right">
                      <v-btn
                        color="error"
                        text
                        icon
                        :disabled="isReadOnly"
                        @click="handleDeleteBusinessHours(index)"
                      >
                        <v-icon>mdi-trash-can</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    label="Descrição completa do negócio"
                    v-model="partner.description"
                    auto-grow
                    :rules="descriptionRules"
                    rows="3"
                    outlined
                    dense
                    hint="Descreva o negócio de forma detalhada"
                    persistent-hint
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="Número do WhatsApp"
                    v-model="partner.whatsapp"
                    v-mask="'(##) #####-####'"
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="Link do Instagram"
                    v-model="partner.instagram"
                    hint="Ex: https://instagram.com/seunegocio"
                    persistent-hint
                    outlined
                    dense
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12">
                  <v-card outlined>
                    <v-card-title class="subtitle-1">Imagens do negócio</v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-file-input
                            label="Logo do negócio"
                            v-model="logoFile"
                            @change="handleConvertForBase64('LOGO')"
                            accept="image/*"
                            prepend-icon="mdi-camera"
                            clearable
                            outlined
                            dense
                            :disabled="isReadOnly"
                            @click:clear="removeImage('LOGO')"
                          />
                          <div v-if="getPreviewImage('LOGO')" class="image-preview">
                            <img
                              :src="getPreviewImage('LOGO')"
                              alt="Logo"
                              @click="openImageDialog(getPreviewImage('LOGO'))"
                            />
                            <v-btn
                              color="error"
                              small
                              icon
                              class="remove-image-btn"
                              :disabled="isReadOnly"
                              @click="removeImage('LOGO')"
                            >
                              <v-icon>mdi-delete-outline</v-icon>
                            </v-btn>
                          </div>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-file-input
                            label="Imagem de banner"
                            v-model="coverFile"
                            @change="handleConvertForBase64('COVER')"
                            accept="image/*"
                            prepend-icon="mdi-camera"
                            clearable
                            outlined
                            dense
                            :disabled="isReadOnly"
                            @click:clear="removeImage('COVER')"
                          />
                          <div v-if="getPreviewImage('COVER')" class="image-preview">
                            <img
                              :src="getPreviewImage('COVER')"
                              alt="Banner"
                              @click="openImageDialog(getPreviewImage('COVER'))"
                            />
                            <v-btn
                              color="error"
                              small
                              icon
                              class="remove-image-btn"
                              :disabled="isReadOnly"
                              @click="removeImage('COVER')"
                            >
                              <v-icon>mdi-delete-outline</v-icon>
                            </v-btn>
                          </div>
                        </v-col>
                        <v-col cols="12">
                          <v-file-input
                            label="Imagens de publicidade"
                            v-model="galleryFiles"
                            @change="handleConvertForBase64('GALLERY', true)"
                            accept="image/*"
                            multiple
                            prepend-icon="mdi-image-multiple"
                            clearable
                            outlined
                            dense
                            :disabled="isReadOnly"
                            @click:clear="clearGalleryFiles"
                          />
                          <v-row v-if="getPreviewImages('GALLERY').length" class="mt-2">
                            <v-col
                              v-for="(image, index) in getPreviewImages('GALLERY')"
                              :key="index"
                              cols="6"
                              sm="4"
                              md="3"
                            >
                              <div class="image-preview">
                                <img
                                  :src="image"
                                  :alt="`Publicidade ${index + 1}`"
                                  @click="openImageDialog(image)"
                                />
                                <v-btn
                                  color="error"
                                  small
                                  icon
                                  class="remove-image-btn"
                                  :disabled="isReadOnly"
                                  @click="removeImage('GALLERY', index)"
                                >
                                  <v-icon>mdi-delete-outline</v-icon>
                                </v-btn>
                              </div>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>

      <div class="d-flex justify-end mt-6">
        <v-btn class="mr-3" text @click="goBack">Cancelar</v-btn>
        <v-btn
          color="#e5183d"
          dark
          :loading="saving || isProcessingImage"
          :disabled="isReadOnly || saving || isProcessingImage"
          @click="handleSave"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salvar alterações
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import { PartnerDetailsType } from "@/types/partner/Partner";
import { GetPartner } from "@/services/partners/GetPartner";
import { GetAllCategories } from "@/services/partners/GetAllCategories";
import { UpdatePartnerStore } from "@/services/partners/UpdatePartnerStore";
import { GetAddress } from "@/services/viaCep/GetAddress";
import { CategoryType } from "@/types/category/Category";

export default defineComponent({
  components: { PageLoading },
  data() {
    return {
      tab: 0,
      loading: true,
      saving: false,
      isProcessingImage: false,
      dialog: false,
      dialogImageSrc: "",
      categories: [] as CategoryType[],
      logoFile: null as File | null,
      coverFile: null as File | null,
      galleryFiles: [] as File[],
      logoPreviewUrl: "",
      coverPreviewUrl: "",
      galleryPreviewUrls: [] as string[],
      businessHoursDay: "",
      businessHoursStartTime: "",
      businessHoursEndTime: "",
      days: [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado",
      ],
      headersHours: [
        { text: "Dia", value: "day" },
        { text: "Abertura", value: "startTime" },
        { text: "Fechamento", value: "endTime" },
        { text: "", value: "actions", align: "end" },
      ],
      descriptionRules: [(v: string) => !v || v.length <= 500 || "Máximo 500 caracteres"],
      partner: {
        id: "",
        name: "",
        nickname: "",
        tax: 0,
        typeCompany: "PJ",
        category: { name: "" },
        categoryId: null,
        phone: "",
        email: "",
        document: "",
        pixkey: "",
        cnaeCode: "",
        address: {
          state: "",
          street: "",
          addressNumber: "",
          neighborhood: "",
          complement: "",
          zipCode: "",
          cityName: "",
          ibge: "",
        },
        users: [],
        associate: [],
        businessHours: [],
        createdAt: "",
        digitalAccountNumber: "",
        digitalAccountDocument: "",
        status: "PENDING",
        indicationKey: "",
        description: "",
        instagram: "",
        whatsapp: "",
        images: [],
      } as PartnerDetailsType,
    };
  },
  computed: {
    isReadOnly(): boolean {
      return (
        this.$store.getters.isFranchiseAssociateInactive ||
        this.$store.getters.isFranchiseAssociatePending
      );
    },
  },
  mounted() {
    void this.loadPage();
  },
  methods: {
    async loadPage() {
      this.loading = true;
      try {
        await Promise.all([this.handleGetPartner(), this.handleGetAllCategories()]);
      } catch {
        this.showSnack("Não foi possível carregar os dados da loja.", "error");
        this.goBack();
      } finally {
        this.loading = false;
      }
    },
    async handleGetPartner() {
      const partnerId = String(this.$route.params.id || "");
      const partnerData = await new GetPartner().execute(partnerId);
      const document = partnerData.document?.startsWith("P")
        ? ""
        : partnerData.document || "";

      this.partner.id = partnerData.id || "";
      this.partner.name = partnerData.name || "";
      this.partner.nickname = partnerData.nickname || partnerData.nickName || "";
      this.partner.document = document;
      this.partner.cnaeCode = partnerData.cnaeCode || "";
      this.partner.category = partnerData.category || { name: "" };
      this.partner.categoryId =
        partnerData.categoryId || (partnerData.category as CategoryType)?.id
          ? Number(partnerData.categoryId || (partnerData.category as CategoryType)?.id)
          : null;
      this.partner.phone = partnerData.phone || "";
      this.partner.email = partnerData.email || "";
      this.partner.status = partnerData.status;
      this.partner.createdAt = partnerData.createdAt || "";
      this.partner.address.street = partnerData.address?.street || "";
      this.partner.address.ibge = partnerData.address?.ibge || "";
      this.partner.address.addressNumber = partnerData.address?.addressNumber || "";
      this.partner.address.neighborhood = partnerData.address?.neighborhood || "";
      this.partner.address.complement = partnerData.address?.complement || "";
      this.partner.address.zipCode = partnerData.address?.zipCode || "";
      this.partner.address.cityName = partnerData.address?.cityName || "";
      this.partner.address.state = partnerData.address?.state || "";
      this.partner.businessHours = partnerData.businessHours || [];
      this.partner.description = partnerData.description || "";
      this.partner.instagram = partnerData.instagram || "";
      this.partner.whatsapp = partnerData.whatsapp || "";
      this.partner.images = (partnerData.images || []).map((img) => ({
        type: img.type,
        image: img.image,
        status: "unchanged",
      }));
      this.partner.typeCompany = document.replace(/\D/g, "").length > 11 ? "PJ" : "PF";
      this.logoPreviewUrl = "";
      this.coverPreviewUrl = "";
      this.galleryPreviewUrls = [];
    },
    async handleGetAllCategories() {
      const response = await new GetAllCategories().execute();
      const data = response.data;
      this.categories = Array.isArray(data) ? data : data?.categories || [];
    },
    syncCompanyType() {
      const digits = (this.partner.document || "").replace(/\D/g, "");
      this.partner.typeCompany = digits.length > 11 ? "PJ" : "PF";
    },
    async handleAddress() {
      const zipCode = this.partner.address.zipCode;
      if (!zipCode || zipCode.replace(/\D/g, "").length !== 8) return;
      const address = await new GetAddress().execute(zipCode);
      if (address && !address.erro) {
        this.partner.address.street = address.logradouro;
        this.partner.address.neighborhood = address.bairro;
        this.partner.address.cityName = address.localidade || "";
        this.partner.address.state = address.uf || "";
        this.partner.address.ibge = address.ibge || "";
      }
    },
    handleAddBusinessHours() {
      this.partner.businessHours.push({
        day: this.businessHoursDay,
        startTime: this.businessHoursStartTime,
        endTime: this.businessHoursEndTime,
      });
      this.businessHoursDay = "";
      this.businessHoursStartTime = "";
      this.businessHoursEndTime = "";
    },
    handleDeleteBusinessHours(index: number) {
      this.partner.businessHours = this.partner.businessHours.filter(
        (_item, itemIndex) => itemIndex !== index
      );
    },
    openImageDialog(src: string) {
      this.dialogImageSrc = src;
      this.dialog = true;
    },
    getPreviewImage(type: "LOGO" | "COVER"): string {
      if (type === "LOGO" && this.logoPreviewUrl) return this.logoPreviewUrl;
      if (type === "COVER" && this.coverPreviewUrl) return this.coverPreviewUrl;
      const imageObj = this.partner.images?.find((img) => img.type === type);
      return imageObj && imageObj.status !== "deleted" ? imageObj.image : "";
    },
    getPreviewImages(type: "GALLERY"): string[] {
      if (this.galleryPreviewUrls.length > 0) return this.galleryPreviewUrls;
      return (
        this.partner.images
          ?.filter((img) => img.type === type && img.status !== "deleted")
          .map((img) => img.image) || []
      );
    },
    handleConvertForBase64(type: "LOGO" | "COVER" | "GALLERY", isMultiple = false) {
      if (this.isProcessingImage) return;
      this.isProcessingImage = true;

      if (isMultiple) {
        if (!this.galleryFiles || this.galleryFiles.length === 0) {
          this.isProcessingImage = false;
          this.clearGalleryFiles();
          return;
        }

        this.galleryPreviewUrls = this.galleryFiles.map((file) =>
          URL.createObjectURL(file)
        );
        const currentCount = (this.partner.images || []).filter(
          (img) => img.type === "GALLERY" && img.status !== "deleted"
        ).length;
        if (currentCount + this.galleryFiles.length > 3) {
          this.showSnack("Você pode adicionar no máximo 3 imagens de publicidade.", "error");
          this.galleryFiles = [];
          this.galleryPreviewUrls = [];
          this.isProcessingImage = false;
          return;
        }

        this.partner.images?.forEach((img) => {
          if (img.type === "GALLERY" && img.status !== "deleted") {
            img.status = "deleted";
          }
        });

        let processedCount = 0;
        this.galleryFiles.forEach((file) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64Image = reader.result as string;
            this.updateImageInPartner(
              "GALLERY",
              base64Image.replace(/^data:[^;]+;base64,/, "")
            );
            processedCount += 1;
            if (processedCount === this.galleryFiles.length) {
              this.isProcessingImage = false;
            }
          };
          reader.onerror = () => {
            this.isProcessingImage = false;
          };
          reader.readAsDataURL(file);
        });
        return;
      }

      const fileToProcess = type === "LOGO" ? this.logoFile : this.coverFile;
      if (!fileToProcess) {
        this.removeImage(type);
        this.isProcessingImage = false;
        return;
      }

      if (type === "LOGO") {
        this.logoPreviewUrl = URL.createObjectURL(fileToProcess);
      } else {
        this.coverPreviewUrl = URL.createObjectURL(fileToProcess);
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result as string;
        this.updateImageInPartner(type, base64Image.replace(/^data:[^;]+;base64,/, ""));
        this.isProcessingImage = false;
      };
      reader.onerror = () => {
        this.isProcessingImage = false;
      };
      reader.readAsDataURL(fileToProcess);
    },
    updateImageInPartner(type: "LOGO" | "COVER" | "GALLERY", base64String: string) {
      if (!this.partner.images) this.partner.images = [];
      if (type === "GALLERY") {
        this.partner.images.push({ type, image: base64String, status: "added" });
        return;
      }
      const existingIndex = this.partner.images.findIndex(
        (img) => img.type === type && img.status !== "deleted"
      );
      if (existingIndex !== -1) {
        this.partner.images[existingIndex].status = "deleted";
      }
      this.partner.images.push({ type, image: base64String, status: "added" });
    },
    removeImage(type: "LOGO" | "COVER" | "GALLERY", index?: number) {
      if (!this.partner.images) return;
      if (type === "GALLERY" && index !== undefined) {
        const galleryImages = this.partner.images.filter(
          (img) => img.type === "GALLERY" && img.status !== "deleted"
        );
        const imageToRemove = galleryImages[index];
        const actualIndex = this.partner.images.findIndex((img) => img === imageToRemove);
        if (actualIndex !== -1) {
          this.partner.images[actualIndex].status = "deleted";
        }
        this.galleryFiles.splice(index, 1);
        this.galleryPreviewUrls.splice(index, 1);
        return;
      }
      const existingIndex = this.partner.images.findIndex(
        (img) => img.type === type && img.status !== "deleted"
      );
      if (existingIndex !== -1) {
        this.partner.images[existingIndex].status = "deleted";
      }
      if (type === "LOGO") {
        this.logoFile = null;
        this.logoPreviewUrl = "";
      } else if (type === "COVER") {
        this.coverFile = null;
        this.coverPreviewUrl = "";
      }
    },
    clearGalleryFiles() {
      this.partner.images?.forEach((img) => {
        if (img.type === "GALLERY" && img.status !== "deleted") {
          img.status = "deleted";
        }
      });
      this.galleryFiles = [];
      this.galleryPreviewUrls = [];
    },
    async handleSave() {
      if (this.isReadOnly) {
        this.showSnack(
          "Sua conta não permite alterar dados de parceiros no momento.",
          "warning"
        );
        return;
      }
      if (!this.partner.name || !this.partner.phone) {
        this.showSnack("Preencha nome e telefone nos dados principais.", "warning");
        this.tab = 0;
        return;
      }

      const zipDigits = (this.partner.address.zipCode || "").replace(/\D/g, "");
      const hasAnyAddress = !!(
        zipDigits ||
        this.partner.address.street ||
        this.partner.address.cityName
      );
      if (
        hasAnyAddress &&
        (zipDigits.length !== 8 ||
          !this.partner.address.street ||
          !this.partner.address.cityName ||
          !this.partner.address.state ||
          !this.partner.address.ibge)
      ) {
        this.showSnack(
          "Preencha o endereço completo, incluindo o CEP para buscar a cidade.",
          "warning"
        );
        this.tab = 1;
        return;
      }

      const payload: PartnerDetailsType = {
        ...this.partner,
        images: (this.partner.images || []).filter((img) => img.status !== "deleted"),
      };

      this.saving = true;
      try {
        await new UpdatePartnerStore().execute(String(this.partner.id), payload);
        this.showSnack("Os dados da loja foram atualizados com sucesso.", "success");
        await this.handleGetPartner();
      } catch {
        this.showSnack("Não foi possível salvar os dados da loja.", "error");
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push({ name: "partners" });
    },
    showSnack(message: string, color: string) {
      this.$store.dispatch("showSnack", { message, color, timer: 4000 });
    },
  },
});
</script>

<style scoped lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.page-title {
  color: #23263a;
  font-weight: 700;
  font-size: 1.45rem;
  margin: 0;
}

.page-subtitle {
  color: #6b7280;
  font-size: 0.95rem;
}

.image-preview {
  position: relative;
  margin-top: 8px;
  display: inline-block;
}

.image-preview img {
  width: 100%;
  max-height: 180px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
}

.remove-image-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: #fff;
}

.preview-dialog-img {
  width: 100%;
  max-height: 80vh;
  object-fit: contain;
}
</style>
